type Prefetcher = () => Promise<unknown>;
type PrefetchOptions = { force?: boolean };

const ROUTE_PREFETCHERS: Record<string, Prefetcher> = {
  '/home': () => import('@/pages/Home'),
  '/programs': () => import('@/pages/Programs'),
  '/live': () => import('@/pages/Live'),
  '/party': () => import('@/pages/Party'),
  '/profile': () => import('@/pages/Profile'),
  '/settings': () => import('@/pages/Settings'),
  '/create': () => import('@/pages/Create'),
  '/auth': () => import('@/pages/Auth'),
  '/free': () => import('@/pages/FreePrograms'),
  '/pricing': () => import('@/pages/Pricing'),
  '/about': () => import('@/pages/About'),
  '/support': () => import('@/pages/Support'),
  '/terms': () => import('@/pages/Terms'),
  '/privacy': () => import('@/pages/Privacy'),
  '/onboarding': () => import('@/pages/Onboarding'),
  '/complete-account': () => import('@/pages/CompleteAccount'),
  '/schedule': () => import('@/pages/MySchedule'),
};

const prefetchedRoutes = new Set<string>();
const prefetchedModules = new Set<string>();

function normalizePath(href: string) {
  const path = href.split('?')[0].split('#')[0];

  if (path === '/' || path === '/discover') return '/home';
  if (path === '/prompts') return '/programs';
  if (path === '/calendar' || path === '/parties') return '/live';

  return path;
}

function isNativeRuntime() {
  if (typeof document !== 'undefined') {
    if (document.documentElement.classList.contains('native-runtime')) {
      return true;
    }
  }

  if (typeof window !== 'undefined') {
    const nativeMarker = (window as Window & { __JP_NATIVE_RUNTIME__?: boolean }).__JP_NATIVE_RUNTIME__;
    if (nativeMarker) return true;
  }

  return false;
}

export function shouldPrefetch() {
  // In the native shells, route chunks are bundled local files. Idle prefetching
  // smooths first navigation without creating extra network pressure.
  if (isNativeRuntime()) return true;

  if (typeof navigator === 'undefined') return false;

  const connection = (navigator as any).connection as
    | { effectiveType?: string; saveData?: boolean; downlink?: number }
    | undefined;

  if (connection?.saveData) return false;

  const effectiveType = connection?.effectiveType;
  if (effectiveType === '2g' || effectiveType === 'slow-2g') return false;

  return true;
}

export function prefetchRoute(href: string, options: PrefetchOptions = {}) {
  if (!import.meta.env.PROD) return;
  if (!options.force && !shouldPrefetch()) return;

  const path = normalizePath(href);
  const prefetcher = ROUTE_PREFETCHERS[path];

  if (!prefetcher || prefetchedRoutes.has(path)) return;

  prefetchedRoutes.add(path);
  const promise = prefetcher();
  promise.catch(() => {
    prefetchedRoutes.delete(path);
  });
  return promise;
}

function prefetchModule(key: string, loader: Prefetcher, options: PrefetchOptions = {}) {
  if (!import.meta.env.PROD) return;
  if (!options.force && !shouldPrefetch()) return;
  if (prefetchedModules.has(key)) return;

  prefetchedModules.add(key);
  const promise = loader();
  promise.catch(() => {
    prefetchedModules.delete(key);
  });
  return promise;
}

export function prefetchProgramDetail() {
  return prefetchModule('program-detail', () => import('@/pages/ProgramDetail'));
}

export function prefetchEventDetail() {
  return prefetchModule('event-detail', () => import('@/pages/EventLanding'));
}

export function prefetchPublicRoutes() {
  return Promise.allSettled([
    prefetchRoute('/auth'),
    prefetchRoute('/free'),
    prefetchRoute('/pricing'),
    prefetchRoute('/about'),
    prefetchRoute('/support'),
  ]);
}

export function prefetchAuthedRoutes() {
  return Promise.allSettled([
    prefetchRoute('/home'),
    prefetchRoute('/live'),
    prefetchRoute('/programs'),
    prefetchRoute('/profile'),
    prefetchRoute('/settings'),
  ]);
}

export function prefetchSharedRoutes() {
  return Promise.allSettled([prefetchRoute('/terms'), prefetchRoute('/privacy')]);
}

export function prefetchNativeCoreRoutes() {
  if (!isNativeRuntime()) return Promise.resolve([]);

  return Promise.allSettled([
    prefetchRoute('/auth', { force: true }),
    prefetchRoute('/onboarding', { force: true }),
    prefetchRoute('/complete-account', { force: true }),
    prefetchRoute('/home', { force: true }),
    prefetchRoute('/programs', { force: true }),
    prefetchRoute('/live', { force: true }),
    prefetchRoute('/settings', { force: true }),
  ]);
}
